import * as React from 'react'
import { useQuery } from '@tanstack/react-query'
import { FormField } from '@/components/templates/entity-form'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { getJSON } from '@/lib/api'
import { apiFieldMessages } from '@/lib/contacts/errors'
import { displayDate } from '@/lib/dates'
import { PaymentAllocationMath } from '@/pages/PaymentAllocationMath'
import {
  formAllocations,
  useAllocationSuggestion,
  usePaymentAllocationQuote,
  type PaymentAllocationQuote,
} from '@/pages/RecordPaymentAllocationFields.queries'
import type { Invoice, InvoicesResponse, PaymentForm } from '@/pages/InvoicesPage.types'

export function OfflinePaymentFields({
  invoice,
  form,
  onChange,
  error,
  onQuoteChange,
}: {
  invoice: Invoice
  form: PaymentForm
  onChange: (form: PaymentForm) => void
  error: Error | null
  onQuoteChange?: (quote: PaymentAllocationQuote | null) => void
}) {
  const paymentTypes = usePaymentTypes()
  const invoices = useOpenInvoices(invoice)
  const [selected, setSelected] = React.useState<Record<number, string>>({ [invoice.id]: '' })
  const latestDefaultRequest = React.useRef(0)
  const [tracksDefaultAllocation, setTracksDefaultAllocation] = React.useState(true)
  const suggestion = useAllocationSuggestion(invoice, form, setSelected, onChange)
  const openInvoices = invoices.data ?? []
  const quote = usePaymentAllocationQuote(invoice.contact_id, form.amount, selected)

  React.useEffect(() => {
    onQuoteChange?.(quote.data?.quote ?? null)
  }, [onQuoteChange, quote.data])

  const updateSelected = (next: Record<number, string>) => {
    setSelected(next)
    onChange({ ...form, allocations: formAllocations(next) })
  }
  const updateAllocation = (invoiceId: number, amount: string | null) => {
    latestDefaultRequest.current += 1
    setTracksDefaultAllocation(false)
    const next = { ...selected }
    if (amount === null) delete next[invoiceId]
    else next[invoiceId] = amount
    updateSelected(next)
  }
  const updatePaymentAmount = (amount: string) => {
    if (!tracksDefaultAllocation) {
      onChange({ ...form, amount })
      return
    }

    onChange({ ...form, amount })
    if (amount.trim() === '') {
      const next = { [invoice.id]: '' }
      setSelected(next)
      onChange({ ...form, amount, allocations: [] })
      return
    }

    latestDefaultRequest.current += 1
    suggestion.mutate({
      amount,
      invoiceId: invoice.id,
      requestId: latestDefaultRequest.current,
      latestRequest: latestDefaultRequest,
    })
  }

  return (
    <>
      <PaymentFields
        form={form}
        paymentTypes={paymentTypes.data ?? []}
        onChange={onChange}
        onAmountChange={updatePaymentAmount}
        error={error}
      />
      {paymentTypes.isError && (
        <Alert variant="destructive">
          Custom Payment Types could not be loaded. Built-in payment methods remain available.
        </Alert>
      )}
      <InvoiceAllocationList
        invoices={invoices}
        openInvoices={openInvoices}
        selected={selected}
        suggestionPending={suggestion.isPending}
        suggestionDisabled={form.amount.trim() === ''}
        onSuggest={() => {
          latestDefaultRequest.current += 1
          setTracksDefaultAllocation(false)
          suggestion.mutate({ amount: form.amount })
        }}
        onAllocationChange={updateAllocation}
      />
      {suggestion.isError && (
        <Alert variant="destructive">An allocation could not be suggested for this amount.</Alert>
      )}
      {apiFieldMessages(error, 'allocations')[0] && (
        <Alert variant="destructive">{apiFieldMessages(error, 'allocations')[0]}</Alert>
      )}
      {quote.isError && (
        <Alert variant="destructive">The Payment totals could not be quoted.</Alert>
      )}
      {quote.data?.quote ? (
        <PaymentAllocationMath
          payment={quote.data.quote.payment}
          allocated={quote.data.quote.allocated}
          accountCredit={quote.data.quote.account_credit}
          accountCreditCents={quote.data.quote.account_credit_cents}
        />
      ) : (
        <p className="text-body-sm text-muted-foreground" role="status">
          {form.amount.trim() === ''
            ? 'Enter an amount to see Payment totals.'
            : 'Updating totals...'}
        </p>
      )}
    </>
  )
}

function useOpenInvoices(invoice: Invoice) {
  return useQuery({
    queryKey: ['invoices', 'payment-allocation', invoice.contact_id],
    queryFn: async () => {
      const data = await getJSON<InvoicesResponse>('/api/v1/invoices?open_balance=true')
      return data.invoices.filter((candidate) => candidate.contact_id === invoice.contact_id)
    },
    retry: false,
  })
}

function InvoiceAllocationList({
  invoices,
  openInvoices,
  selected,
  suggestionPending,
  suggestionDisabled,
  onSuggest,
  onAllocationChange,
}: {
  invoices: ReturnType<typeof useOpenInvoices>
  openInvoices: Invoice[]
  selected: Record<number, string>
  suggestionPending: boolean
  suggestionDisabled: boolean
  onSuggest: () => void
  onAllocationChange: (invoiceId: number, amount: string | null) => void
}) {
  return (
    <section aria-labelledby="open-invoices-heading">
      <div className="mb-1.5 flex items-center justify-between gap-3">
        <h2 id="open-invoices-heading" className="text-caption font-medium text-foreground">
          Apply to open Invoices
        </h2>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 border-dashed px-2 text-caption"
          disabled={suggestionPending || suggestionDisabled}
          onClick={onSuggest}
        >
          {suggestionPending ? 'Suggesting...' : 'Suggest oldest first'}
        </Button>
      </div>
      {invoices.isLoading && (
        <p className="py-2 text-body-sm text-muted-foreground">Loading open Invoices...</p>
      )}
      {invoices.isError && <Alert variant="destructive">Open Invoices could not be loaded.</Alert>}
      {!invoices.isLoading && !invoices.isError && openInvoices.length === 0 && (
        <p className="border-y border-border py-2 text-body-sm text-muted-foreground">
          No open Invoices.
        </p>
      )}
      {openInvoices.length > 0 && (
        <div className="divide-y divide-border border-y border-border">
          {openInvoices.map((openInvoice) => (
            <PaymentAllocationRow
              key={openInvoice.id}
              invoice={openInvoice}
              checked={Object.hasOwn(selected, openInvoice.id)}
              amount={selected[openInvoice.id] ?? ''}
              onCheckedChange={(checked) => onAllocationChange(openInvoice.id, checked ? '' : null)}
              onAmountChange={(amount) => onAllocationChange(openInvoice.id, amount)}
            />
          ))}
        </div>
      )}
    </section>
  )
}

function PaymentFields({
  form,
  paymentTypes,
  onChange,
  onAmountChange,
  error,
}: {
  form: PaymentForm
  paymentTypes: CustomPaymentType[]
  onChange: (form: PaymentForm) => void
  onAmountChange: (amount: string) => void
  error: Error | null
}) {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <FormField
        label="Amount"
        htmlFor="payment-amount"
        error={apiFieldMessages(error, 'amount')[0]}
      >
        <Input
          id="payment-amount"
          inputMode="decimal"
          value={form.amount}
          placeholder="0.00"
          onChange={(event) => onAmountChange(event.target.value)}
        />
      </FormField>
      <FormField
        label="Method"
        error={
          apiFieldMessages(error, 'method')[0] ?? apiFieldMessages(error, 'custom_payment_type')[0]
        }
      >
        <Select
          value={form.customPaymentTypeId ? String(form.customPaymentTypeId) : form.method}
          onValueChange={(value) => {
            const custom = paymentTypes.find((type) => String(type.id) === value)
            onChange({
              ...form,
              method: custom ? 'other' : (value as PaymentForm['method']),
              customPaymentTypeId: custom?.id,
            })
          }}
        >
          <SelectTrigger aria-label="Method">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="cash">Cash</SelectItem>
            <SelectItem value="check">Check</SelectItem>
            <SelectItem value="wire">Wire</SelectItem>
            <SelectItem value="other">Other</SelectItem>
            {paymentTypes.map((type) => (
              <SelectItem key={type.id} value={String(type.id)}>
                {type.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FormField>
      <FormField label="Reference" htmlFor="payment-reference">
        <Input
          id="payment-reference"
          value={form.reference}
          onChange={(event) => onChange({ ...form, reference: event.target.value })}
        />
      </FormField>
      <FormField
        label="Payment date"
        htmlFor="payment-paid-on"
        error={apiFieldMessages(error, 'paid_on')[0]}
      >
        <Input
          id="payment-paid-on"
          type="date"
          value={form.paidOn}
          onChange={(event) => onChange({ ...form, paidOn: event.target.value })}
        />
      </FormField>
    </div>
  )
}

interface CustomPaymentType {
  id: number
  name: string
}

function usePaymentTypes() {
  return useQuery({
    queryKey: ['custom-payment-types', 'available'],
    queryFn: async () => {
      const data = await getJSON<{ custom_payment_types: CustomPaymentType[] }>(
        '/api/v1/custom_payment_types?available=true',
      )
      return data.custom_payment_types ?? []
    },
    retry: false,
  })
}

function PaymentAllocationRow({
  invoice,
  checked,
  amount,
  onCheckedChange,
  onAmountChange,
}: {
  invoice: Invoice
  checked: boolean
  amount: string
  onCheckedChange: (checked: boolean) => void
  onAmountChange: (amount: string) => void
}) {
  return (
    <div className="flex items-center gap-3 py-2">
      <Checkbox
        id={`payment-invoice-${invoice.id}`}
        checked={checked}
        onCheckedChange={(value) => onCheckedChange(value === true)}
      />
      <label
        htmlFor={`payment-invoice-${invoice.id}`}
        className="min-w-0 flex-1 cursor-pointer leading-snug"
      >
        <span className="block text-body-sm font-medium text-foreground">#{invoice.number}</span>
        <span className="block truncate text-caption text-muted-foreground">
          {invoice.origin} · due {displayDate(invoice.due_on)}
        </span>
      </label>
      <span className="whitespace-nowrap text-caption text-muted-foreground">
        Open {invoice.balance}
      </span>
      <Input
        aria-label={`Allocation for Invoice ${invoice.number}`}
        className="h-8 w-28 text-right tabular-nums"
        inputMode="decimal"
        value={amount}
        disabled={!checked}
        onChange={(event) => onAmountChange(event.target.value)}
      />
    </div>
  )
}

export type { PaymentAllocationQuote }
